import {
  Box,
  Typography,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import React from 'react';

interface TabConfig {
  label: string;
  rows?: any[];
  columns?: GridColDef[];
  component?: React.ReactNode;   // added cleanly here
  modalRenderer?: (row: any) => React.ReactNode;
}

interface StandardDataGridProps {
  isLoading: boolean;
  tabs?: TabConfig[];
  singleGrid?: {
    rows: any[];
    columns: GridColDef[];
    modalRenderer?: (row: any) => React.ReactNode;
  };
  height?: string;
  onRowClick?: (row: any) => void;
}

const StandardDataGrid: React.FC<StandardDataGridProps> = ({
  isLoading,
  tabs,
  singleGrid,
  height = 'calc(100vh - 150px)',
  onRowClick,
}) => {
  const [selectedRow, setSelectedRow] = React.useState<any>(null);
  const [activeTab, setActiveTab] = React.useState(0);

  const handleRowClick = (params: any) => {
    setSelectedRow(params.row);
    onRowClick?.(params.row);
  };

  const currentTab = tabs?.[activeTab];

  const renderModalContent = () => {
    if (tabs && currentTab?.modalRenderer) return currentTab.modalRenderer(selectedRow);
    if (singleGrid?.modalRenderer) return singleGrid.modalRenderer(selectedRow);
    return null;
  };

  const hasValidModalRenderer = Boolean(
    (tabs && currentTab?.modalRenderer) || singleGrid?.modalRenderer
  );

  return (
    <Box sx={{ height, width: '100%', display: 'flex', flexDirection: 'column' }}>
      {tabs && (
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => {
            setActiveTab(newValue);
            setSelectedRow(null);
          }}
          variant="fullWidth"
          sx={{ mb: 2, backgroundColor: '#ffffff' }}
        >
          {tabs.map((t, idx) => (
            <Tab key={idx} label={t.label} />
          ))}
        </Tabs>
      )}

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {isLoading ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography>Loading...</Typography>
          </Box>
        ) : currentTab?.component ? (
          currentTab.component
        ) : (
          <DataGrid
            rows={tabs ? currentTab?.rows ?? [] : singleGrid?.rows ?? []}
            columns={tabs ? currentTab?.columns ?? [] : singleGrid?.columns ?? []}
            getRowHeight={() => 'auto'}
            initialState={{ pagination: { paginationModel: { pageSize: 100, page: 0 } } }}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            sx={{
              fontFamily: 'Segoe UI',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f0f0f0',
                fontWeight: 'bold',
                fontSize: '.8rem',
              },
              '& .MuiDataGrid-row': {
                '&:nth-of-type(even)': {
                  backgroundColor: '#f9f9f9', // light zebra row
                },
                '&:nth-of-type(odd)': {
                  backgroundColor: '#ffffff', // default
                },
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#e0f7fa',
              },
              '& .MuiDataGrid-cell': {
                whiteSpace: 'pre-wrap',
                alignItems: 'start',
                paddingTop: '8px',
                paddingBottom: '8px',
              },
              '& .MuiDataGrid-footerContainer': {
                backgroundColor: '#ffffff',
              },
            }}
          />
        )}
      </Box>

      {selectedRow && hasValidModalRenderer && (
        <Dialog
          open={true}
          onClose={() => setSelectedRow(null)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            Details
            <IconButton onClick={() => setSelectedRow(null)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>{renderModalContent()}</DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default StandardDataGrid;
